/**
 * Inherent risk card: the combined country + industry level, with each input as a detail row.
 */
import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchResourceWithPost } from '@app/src/api/fetchHooks'
import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import RiskIndexInfoDrawerView from '@app/src/components/Drawer/Views/RiskIndexInfoDrawerView'
import { useAmplitude } from '@app/src/context/AmplitudeContext'
import useLocalisedCountryName from '@app/src/hooks/useLocalisedCountryName'
import SimpleRiskChip from '@app/src/pages/Assessments/AssessmentCompanyPage/SimpleRiskChip'
import { CONFIGURATION_PAGE_IDS } from '@app/src/pages/Configurations/ConfigurationsScene'
import { ActivateInConfigurationButton } from '@app/src/pages/TopicalRisk/Drawer/ActivateInConfigurationButton'
import { CollapsibleRiskCard } from '@app/src/pages/TopicalRisk/Drawer/CollapsibleRiskCard'
import {
  getContributingRiskIndexTypes,
  getInherentRiskDescriptionKey,
  getInherentRiskStatus,
  isRiskIndexDeactivated,
  isSubIndustryMissing,
  shouldShowRiskIndexRow,
} from '@app/src/pages/TopicalRisk/Drawer/inherentRiskLogic'
import { InherentRiskMethodologyLink } from '@app/src/pages/TopicalRisk/Drawer/InherentRiskMethodologyLink'
import { RiskCardListItem } from '@app/src/pages/TopicalRisk/Drawer/RiskCardListItem'
import { getHighestCountryRiskStatus } from '@app/src/pages/TopicalRisk/Drawer/topicalRiskDrawerUtils'
import type { Provider } from '@app/src/types/organizations'
import type { RiskType } from '@app/src/types/resourceExplorer'
import { RiskStatus } from '@app/src/types/resourceExplorer'
import type { SustainabilityTopicRisk } from '@app/src/types/topicalRisk'
import {
  getCountryVariantTranslationKey,
  getRelevantCountryRisks,
  getRelevantIndustryRisks,
} from '@app/src/utils/riskStatusHelpers'
import { AmplitudeTrackingEvents } from '@app/src/wf-constants'
import { InfoOutlined } from '@mui/icons-material'
import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'

export interface InherentRiskCardProps {
  sustainabilityTopicRisk: SustainabilityTopicRisk
}

export const InherentRiskCard: React.FC<InherentRiskCardProps> = ({ sustainabilityTopicRisk }) => {
  const { formatMessage } = useIntl()
  const { openDrawer } = useDrawer()
  const { trackEvent } = useAmplitude()
  const { getLocalisedCountryName } = useLocalisedCountryName()

  const countryRiskStatus = getHighestCountryRiskStatus(
    sustainabilityTopicRisk.headquarterRiskStatus,
    sustainabilityTopicRisk.operationRiskStatus,
  )
  const industryRiskStatus = sustainabilityTopicRisk.industryRiskStatus

  const { data: provider } = useFetchResourceWithPost<Provider, string[]>({
    key: [FetchKey.Provider, sustainabilityTopicRisk.providerId],
    endpoint: endpoints.provider(sustainabilityTopicRisk.providerId),
    body: [],
    options: { enabled: industryRiskStatus === RiskStatus.NotApplicable },
  })
  const hasSubIndustry = provider ? Boolean(provider.subIndustryId) : undefined
  const isMissingSubIndustry = isSubIndustryMissing(industryRiskStatus, hasSubIndustry)

  const countryNames = useMemo(
    () =>
      getRelevantCountryRisks(sustainabilityTopicRisk.countryRisks, countryRiskStatus, getLocalisedCountryName).map(
        group =>
          `${group.countries.join(', ')} (${formatMessage({ id: getCountryVariantTranslationKey(group.variant) })})`,
      ),
    [sustainabilityTopicRisk.countryRisks, countryRiskStatus, getLocalisedCountryName, formatMessage],
  )

  const industryNames = useMemo(
    () => getRelevantIndustryRisks(sustainabilityTopicRisk.industryRisks, industryRiskStatus).map(risk => risk.name),
    [sustainabilityTopicRisk.industryRisks, industryRiskStatus],
  )

  // Assume only one risk type per country risks and industry risks for now
  const countryRiskType = sustainabilityTopicRisk.countryRisks?.[0]?.riskType
  const industryRiskType = sustainabilityTopicRisk.industryRisks?.[0]?.riskType

  const contributingRiskIndexTypes = getContributingRiskIndexTypes(sustainabilityTopicRisk)
  const inherentRiskStatus = getInherentRiskStatus(sustainabilityTopicRisk)

  const openRiskIndex = (riskType: RiskType, riskIndexName: string) => {
    trackEvent({
      name: AmplitudeTrackingEvents.Analyze.SupplierRisk.ClickedRiskIndex,
      eventProps: {
        providerId: sustainabilityTopicRisk.providerId,
        inherentRiskType: riskIndexName,
        riskType: riskType.name,
      },
    })
    openDrawer(<RiskIndexInfoDrawerView riskType={riskType} />)
  }

  const rowDefinitions = [
    {
      name: formatMessage({ id: 'general.countryRisk' }),
      riskStatus: countryRiskStatus,
      names: countryNames,
      riskType: countryRiskType,
      emptyMessageId: 'topicalRisk.noLocationRisk',
      isMissingSubIndustry: false,
    },
    {
      name: formatMessage({ id: 'general.industryRisk' }),
      riskStatus: industryRiskStatus,
      names: industryNames,
      riskType: industryRiskType,
      emptyMessageId: 'topicalRisk.noIndustryRisk',
      isMissingSubIndustry,
    },
  ]

  // Drop indices with nothing to say, then let the activated one lead.
  const visibleRows = rowDefinitions
    .filter(row => shouldShowRiskIndexRow(row.riskStatus, row.isMissingSubIndustry))
    .sort(
      (left, right) =>
        Number(isRiskIndexDeactivated(left.riskStatus)) - Number(isRiskIndexDeactivated(right.riskStatus)),
    )

  return (
    <CollapsibleRiskCard
      title={formatMessage({ id: 'topicalRisk.sectionHeader.inherentRisk' })}
      statusElement={<SimpleRiskChip larger outlined riskStatus={inherentRiskStatus} showBackgroundForInactive />}
      description={formatMessage(
        { id: getInherentRiskDescriptionKey(contributingRiskIndexTypes) },
        {
          link: (chunks: React.ReactNode) => <InherentRiskMethodologyLink>{chunks}</InherentRiskMethodologyLink>,
        },
      )}
    >
      {visibleRows.length
        ? visibleRows.map(row => {
            const isDeactivated = isRiskIndexDeactivated(row.riskStatus)
            const { riskType } = row
            // Nothing to look up or rate until the supplier has a sub-industry.
            const hasLevel = !isDeactivated && !row.isMissingSubIndustry

            const getSupportingText = () => {
              if (isDeactivated) return formatMessage({ id: 'schemas.risk.status.GDeactivated' })
              if (row.isMissingSubIndustry) return formatMessage({ id: 'topicalRisk.drawer.noSupplierSubIndustry' })

              return row.names.join(', ') || formatMessage({ id: row.emptyMessageId })
            }

            const renderTrailingElement = () => {
              if (isDeactivated)
                return (
                  <ActivateInConfigurationButton
                    configurationsPage={CONFIGURATION_PAGE_IDS.RiskScreening}
                    noAccessTooltipId="topicalRisk.drawer.activateRiskIndexNoAccess"
                  />
                )
              if (!hasLevel) return undefined

              return <SimpleRiskChip outlined riskStatus={row.riskStatus} showBackgroundForInactive />
            }

            return (
              <RiskCardListItem
                key={row.name}
                name={row.name}
                supportingText={getSupportingText()}
                icon={hasLevel ? <InfoOutlined fontSize="small" /> : undefined}
                iconLabel={formatMessage({ id: 'topicalRisk.viewRiskIndex' })}
                onIconClick={riskType && hasLevel ? () => openRiskIndex(riskType, row.name) : undefined}
                trailingElement={renderTrailingElement()}
              />
            )
          })
        : undefined}
    </CollapsibleRiskCard>
  )
}

export default InherentRiskCard
